import React, {useState} from "react";
import cx from "classnames";
import Typography from "@mui/material/Typography";

import { IndeterminateCheckBox, AddBox } from "@vedmarket/ui-kit";
import {formatHSCode} from "../../../../../../../../../../../../../../../../../utils/helpers";

import ModalCode from "../../../../../../../../../../../../../customs/components/modal_code";
import {useLazyGetTnvedQuery} from "../../../../../../../../../../../../../../../../../api/tnved";
import {useLazyGetDeclarationsQuery} from "../../../../../../../../../../../../../../../../../api/dictionary";

import { COLORS } from "../../../../../../../../../../../../../../../../../config/constants";

import './tnved-tree-node.scss'

const TnvedTreeNode: React.FC<TnvedTreeItem> = (
  { node, depth, expandedIds, onToggle, activeId, index, siblingsCount, handleApplyCode }
) => {

  const [ openModalCode, setOpenModalCode ] = useState(false);

  const [ codeToCheck, setCodeToCheck ] = useState('');

  const [ tnvedInfo, setTnvedInfo ] = useState<TnvedInfo>();

  const [ tnvedRequest ] = useLazyGetTnvedQuery()

  const [ declarationsRequest ] = useLazyGetDeclarationsQuery()

  const hasChildren = node.has_children;

  const expanded = expandedIds.has(node.id);

  const isActive = activeId === node.id;
  const iconColor = isActive ? COLORS.BLUE : COLORS.LIGHT_ACTION;
  const Icon = expanded ? IndeterminateCheckBox : AddBox;

  const isLastSibling = index === siblingsCount - 1;
  const isLastRoot = depth === 0 && isLastSibling;
  const hasDeepIndent = depth >= 3;
  const iconInset = 6 + depth * 20 + (hasDeepIndent ? 2 : 0);

  const isHSCode = node.code.toString().length === 10;
  const isPartialCode = /^\d{4,9}$/.test(node.code);
  const codeGroups = node.code.match(/^(.{1,4})(.{1,2})?(.{1,3})?(.{1})?$/);
  const partialCode = codeGroups?.slice(1).filter(Boolean).join(' ');
  const displayCode = isPartialCode ? `${partialCode} ...` : formatHSCode(node.code);

  const handleOpenCodeModal = (code: string) => {
    setCodeToCheck(code);
    declarationsRequest({ code: code });
    tnvedRequest(code)?.then((response) => {
      setTnvedInfo(response?.data);
      setOpenModalCode(true);
    });
  }

  const handleInsertCode = () => {
    handleApplyCode(node.code);
  }

  return (
    <>
      <div className={cx('tnved-tree-node', { 'tnved-tree-node_last-root': isLastRoot })}>
        {(!isLastSibling || isLastRoot) && (
          <div
            className={cx('tnved-tree-node__connector', { 'tnved-tree-node__connector_last-root': isLastRoot })}
            style={{ left: 104 + iconInset + 9 }}
            aria-hidden='true'
          />
        )}
        <div className='tnved-tree-node__row'>
          <Typography
            component='button'
            type='button'
            variant='caption'
            className={cx('tnved-tree-node__code', {
              'tnved-tree-node__code_isActive': isActive,
            })}
            sx={{
              color: isActive ? COLORS.BLUE : COLORS.BLACK_DARK,
              fontWeight: isActive ? 700 : 400,
              fontSize: '12px',
              lineHeight: '18px',
              letterSpacing: 0,
            }}
            onClick={(event) => {
              event.stopPropagation();

              if (hasChildren) {
                onToggle(node.id, node.code);
              }

              if (isHSCode) {
                handleOpenCodeModal(node.code);
              }
            }}
          >
            {displayCode}
          </Typography>
          <div
            className='tnved-tree-node__content'
            style={{ paddingLeft: iconInset }}
          >
            <div
              className={cx('tnved-tree-node__info', {
                'tnved-tree-node__info_expandable': hasChildren,
                'tnved-tree-node__info_deep': hasDeepIndent,
              })}
              onClick={(event) => {
                if (hasChildren) {
                  event.stopPropagation();
                  onToggle(node.id);
                }
              }}
            >
              {hasChildren ? (
                <Icon className='tnved-tree-node__icon' color={COLORS.LIGHT_ACTION} />
              ) : (
                <div
                  className='tnved-tree-node__leaf'
                  style={{ color: iconColor }}
                />
              )}
              <Typography
                variant='caption'
                className='tnved-tree-node__title'
                sx={{
                  color: COLORS.BLACK_DARK,
                  fontSize: '12px',
                  lineHeight: '18px',
                  letterSpacing: 0,
                }}
              >
                {node.name}
              </Typography>
            </div>
          </div>
        </div>
        {expanded && !!node.children?.length && (
          <div className='tnved-tree-node__children'>
            {node.children.map((child, childIndex) => (
              <TnvedTreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                expandedIds={expandedIds}
                onToggle={onToggle}
                activeId={activeId}
                index={childIndex}
                siblingsCount={node.children!.length}
                handleApplyCode={handleApplyCode}
              />
            ))}
          </div>
        )}
      </div>
      <ModalCode
        isOpen={openModalCode}
        onClose={() => setOpenModalCode(false)}
        code={codeToCheck}
        tnvedInfo={tnvedInfo}
        isHelp
        handleApplyCode={handleInsertCode}
        insideModal
      />
    </>

  );
};

export default TnvedTreeNode
